import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, EmptyState, LoadingList, SectionHeader } from '@timely/ui';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { supabase } from '../lib/supabaseClient';
import { describeAuditEntry, type AuditRow } from '../team/auditText';

/**
 * The audit log: who did what in this company, and when.
 *
 * Read straight from `audit_log` with the anon key. The table's only policy is
 * `has_cap(workspace_id, 'workspace.audit.view')`, so the scoping is the
 * database's and this screen does no filtering of its own — somebody without
 * the capability gets an empty list from the server, not a list this file
 * decided not to render.
 *
 * Bounded on purpose. A company that has been running for a year has a lot of
 * these, and a screen that loads all of them is a screen that stops working
 * exactly when the history becomes worth reading. A page at a time, oldest
 * stays where it is.
 *
 * What is deliberately *not* here: anything secret. The rows carry names, role
 * ids and capability lists — never a token, never an encrypted key, never the
 * body of somebody's note about a customer.
 */

const PAGE = 50;

export function AuditLog() {
    const { workspace } = useActiveWorkspace();
    const [rows, setRows] = useState<AuditRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [more, setMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Dropped if the company changed while this was in flight.
    const issuedFor = useRef(workspace.id);

    const loadPage = useCallback(async (from: number) => {
        issuedFor.current = workspace.id;
        if (from === 0) setLoading(true);
        setError(null);

        const { data, error: queryError } = await supabase()
            .from('audit_log')
            .select('id, actor_name, actor_user_id, action, target_kind, target_id, metadata, created_at')
            .eq('workspace_id', workspace.id)
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .range(from, from + PAGE - 1);

        if (issuedFor.current !== workspace.id) return;

        if (queryError) {
            setError('We could not load the history. Try again in a moment.');
            setLoading(false);
            return;
        }

        const page = (data ?? []) as AuditRow[];
        setRows(previous => (from === 0 ? page : [...previous, ...page]));
        setMore(page.length === PAGE);
        setLoading(false);
    }, [workspace.id]);

    useEffect(() => { void loadPage(0); }, [loadPage]);

    return (
        <Page>
            <SectionHeader
                title="History"
                subtitle="Administrative changes in this company. Not customer notes, and never a secret."
                actions={
                    <Button variant="secondary" onClick={() => { void loadPage(0); }}>Refresh</Button>
                }
            />

            {error && (
                <div className="mt-4">
                    <FormNotice tone="error">{error}</FormNotice>
                </div>
            )}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading the history" rows={6} />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Nothing recorded yet"
                        hint="Inviting somebody, changing a role or editing company settings will show up here."
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {rows.map(row => {
                            const described = describeAuditEntry(row);
                            return (
                                <li key={row.id} className="px-5 py-3.5 flex items-start gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[14px] text-label leading-snug">
                                            {described.sentence}
                                        </div>
                                        {described.detail && (
                                            <div className="text-[12.5px] text-label-3 mt-1 leading-snug">
                                                {described.detail}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-none text-right">
                                        <time
                                            dateTime={row.created_at}
                                            className="text-[12.5px] text-label-3 num block"
                                            title={new Date(row.created_at).toISOString()}
                                        >
                                            {formatWhen(row.created_at, workspace.timezone, workspace.locale)}
                                        </time>
                                        <Badge className="mt-1">{described.kind}</Badge>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            {more && !loading && (
                <div className="mt-5 text-center">
                    <Button variant="secondary" onClick={() => { void loadPage(rows.length); }}>
                        Show older
                    </Button>
                </div>
            )}
        </Page>
    );
}

/**
 * The time, in the company's own zone and format.
 *
 * Read from the workspace row, never from a constant: the single-tenant product
 * had one country's zone and number format written into twenty-five files, and a
 * company abroad reading a timestamp shifted by hours is how an audit trail
 * stops being evidence.
 */
function formatWhen(iso: string, timezone: string, locale: string): string {
    try {
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'medium', timeStyle: 'short', timeZone: timezone,
        }).format(new Date(iso));
    } catch {
        // An invalid zone or locale on the company row must not blank the screen.
        return new Date(iso).toISOString().replace('T', ' ').slice(0, 16);
    }
}
